import { useEffect, useRef } from 'react'
import { NAV, pathFor } from '../nav/registry'
import { useLinkProps } from '../routing/router'
import styles from './SideNav.module.css'

function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  )
}

function NavLink({
  to,
  label,
  current,
  onNavigate,
}: {
  to: string
  label: string
  current: boolean
  onNavigate: () => void
}) {
  const link = useLinkProps(to)
  return (
    <li>
      <a
        {...link}
        className={styles.link}
        aria-current={current ? 'page' : undefined}
        onClick={(e) => {
          link.onClick(e)
          if (!e.defaultPrevented) return
          onNavigate()
        }}
        data-testid={`nav-link-${to === '/' ? 'home' : to.slice(1)}`}
      >
        {label}
      </a>
    </li>
  )
}

export interface SideNavProps {
  currentSlug: string
  /** Which groups the reader has explicitly toggled, by group id. */
  openGroups: Record<string, boolean>
  /** Takes the group's current effective state so the flip cannot desync. */
  onToggleGroup: (id: string, expanded: boolean) => void
  /** Drawer state; only meaningful under 960px. */
  open: boolean
  onClose: () => void
}

export function SideNav({
  currentSlug,
  openGroups,
  onToggleGroup,
  open,
  onClose,
}: SideNavProps) {
  const ref = useRef<HTMLElement>(null)

  // The drawer is a modal-ish surface on small screens: Escape closes it.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      {open && (
        <div
          className={styles.scrim}
          onClick={onClose}
          data-testid="nav-scrim"
          aria-hidden="true"
        />
      )}
      <nav
        id="side-nav"
        ref={ref}
        className={styles.nav}
        data-open={open ? 'true' : 'false'}
        aria-label="Documentation"
        data-testid="side-nav"
      >
        <ul className={styles.groups}>
          {NAV.map((group) => {
            const holdsCurrent = group.items.some(
              (item) => item.slug === currentSlug
            )
            // A group the reader has not touched follows the current page;
            // once toggled, their choice wins until they toggle it back.
            const expanded = openGroups[group.id] ?? holdsCurrent
            return (
              <li key={group.id} className={styles.group}>
                <button
                  type="button"
                  className={styles.summary}
                  aria-expanded={expanded}
                  aria-controls={`nav-group-${group.id}`}
                  onClick={() => onToggleGroup(group.id, expanded)}
                  data-testid={`nav-group-${group.id}`}
                >
                  <span className={styles.chevron} data-expanded={expanded}>
                    <Chevron />
                  </span>
                  {group.title}
                </button>
                <ul
                  id={`nav-group-${group.id}`}
                  className={styles.items}
                  hidden={!expanded}
                >
                  {group.items.map((item) => (
                    <NavLink
                      key={item.slug}
                      to={pathFor(item.slug)}
                      label={item.name}
                      current={item.slug === currentSlug}
                      onNavigate={onClose}
                    />
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
      </nav>
    </>
  )
}
